/**
 * Ally Chatbot API Utilities
 *
 * Session CRUD operations only.
 * Chat streaming is handled by the useChat() hook from @ai-sdk/react.
 */

// Get the API base URL
export const getApiBaseUrl = () => {
  // In development, API runs on port 4000
  // In production, it's on the same origin
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:4000';
  }
  return window.location.origin;
};

// Generic request helper for Ally endpoints
const request = async (path, options = {}) => {
  const url = `${getApiBaseUrl()}${path}`;
  const { headers, ...rest } = options;

  const fetchOptions = {
    credentials: 'include', // Include cookies for authentication
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    ...rest,
  };

  const response = await fetch(url, fetchOptions);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error = new Error(errorData.message || `Request failed with status ${response.status}`);
    error.status = response.status;
    error.data = errorData;
    throw error;
  }

  return response.json();
};

/**
 * Start a new Ally session
 *
 * @param {string|null} listingTypeId - Optional listing type ID
 * @returns {Promise<Object>} Session data with sessionId, greeting, resuming flag
 */
export const startSession = async (listingTypeId = null) => {
  return request('/api/ally/session/start', {
    method: 'POST',
    body: JSON.stringify({ listingTypeId }),
  });
};

/**
 * Get session details
 *
 * @param {string} sessionId - Session UUID
 * @returns {Promise<Object>} Session details including messages and extractedFields
 */
export const getSession = async sessionId => {
  return request(`/api/ally/session/${sessionId}`);
};

/**
 * List user's sessions
 *
 * @param {number} limit - Max sessions to return (default: 10)
 * @returns {Promise<Object>} Object with sessions array and count
 */
export const getSessions = async (limit = 10) => {
  return request(`/api/ally/sessions?limit=${limit}`);
};

/**
 * Delete a session
 *
 * @param {string} sessionId - Session UUID
 * @returns {Promise<Object>} Success response
 */
export const deleteSession = async sessionId => {
  return request(`/api/ally/session/${sessionId}`, {
    method: 'DELETE',
  });
};

